import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { outreach, outreachQueries } from '@round/api';
import { tableHelpers, toast } from '@round/creatorbase-ui';
import {
    useAddOutreachResultMutation,
    useArchiveOutreachResultMutation,
    useUnarchiveOutreachResultMutation,
} from '@round/creatorbase-helpers/api-hooks';
import { usePatchOutreachResultsCache } from './usePatchOutreachResultsCache';

const addUtils = outreachQueries.outreachRequestResults.utils.add;
const archiveUtils = outreachQueries.outreachRequestResults.utils.archive;
const unarchiveUtils = outreachQueries.outreachRequestResults.utils.unarchive;

const NO_ACCOUNT_ERROR = "Couldn't reach this creator's account. Please try again";

/** Results are collected from TikTok, so a campaign for one is a TikTok campaign. */
export const ADD_TO_CAMPAIGN_PLATFORM = 'tiktok';

/**
 * Loose enough to accept a table's loading placeholder row too — the row is called unconditionally,
 * since hooks can't be called only once a row turns out to carry a real result. A `LoadingRow`'s index
 * signature only resolves `.id`/`.status` to `undefined` when read off the union itself — a plain
 * `{ id, status }` object type isn't a valid stand-in, since `LoadingRow` doesn't structurally satisfy it.
 */
type ResultActionsTarget = tableHelpers.WithLoadingRow<Pick<outreach.OutreachResult, 'id' | 'status'>>;

/**
 * The add/archive/unarchive/add-to-campaign behaviour a result offers, shared between its row in the
 * table and its expanded creator-details panel — each mounts its own instance, and the cache patch on
 * success is what keeps both, and every other row for the same result, in sync.
 */
export function useResultActions(result: ResultActionsTarget) {
    const queryClient = useQueryClient();
    const patchResultsCache = usePatchOutreachResultsCache();

    const { add, addAsync, isLoading: isAdding } = useAddOutreachResultMutation(result.id, queryClient);
    const { archive, isLoading: isArchiving } = useArchiveOutreachResultMutation(result.id, queryClient);
    const { unarchive, isLoading: isUnarchiving } = useUnarchiveOutreachResultMutation(result.id, queryClient);

    const [isCampaignPickerOpen, setIsCampaignPickerOpen] = useState(false);
    // The menu hands focus back to its trigger as it closes, which reads as focus leaving the picker
    // it has just opened — and dismisses it. Opening one is what waives that handover.
    const isOpeningCampaignPicker = useRef(false);
    // Adding to a campaign adds to the base first, and the row is held back from that answer until
    // the post lands: writing it in straight away would swap the actions under an open picker.
    const pendingAdd = useRef<outreach.OutreachResult | null>(null);

    const patchResultCache = (updated: outreach.OutreachResult) => patchResultsCache([updated]);

    const showPendingAdd = () => {
        if (pendingAdd.current) {
            patchResultCache(pendingAdd.current);
            pendingAdd.current = null;
        }
    };

    // These are only ever wired up to buttons that render after the caller has confirmed the row
    // carries a real result — a loading placeholder's `id` is never actually acted on.
    const id = result.id as number;

    const handleAdd = () =>
        add({ id }, { onSuccess: patchResultCache, onError: (error) => toast.error(addUtils.getErrorMessage(error)) });

    const handleArchive = () =>
        archive(
            { id },
            { onSuccess: patchResultCache, onError: (error) => toast.error(archiveUtils.getErrorMessage(error)) }
        );

    const handleUnarchive = () =>
        unarchive(
            { id },
            { onSuccess: patchResultCache, onError: (error) => toast.error(unarchiveUtils.getErrorMessage(error)) }
        );

    // A post is made against the creator's account in the client's base, so a row that has none yet
    // is added first — the same add the button beside it makes. A save retried after the post was
    // refused reuses the account the first one made rather than asking for another.
    const resolveAccountId = async () => {
        if (!pendingAdd.current) {
            try {
                pendingAdd.current = await addAsync({ id });
            } catch (error) {
                toast.error(addUtils.getErrorMessage(error as Error));
                throw error;
            }
        }

        // An add that answers without an account leaves the post nothing to be made against. The
        // save is stopped here rather than by the form, which says nothing about a refusal — it
        // leaves that to whoever fetched the account.
        const accountId = pendingAdd.current.client_influencer_account_id;
        if (!accountId) {
            toast.error(NO_ACCOUNT_ERROR);
            throw new Error(NO_ACCOUNT_ERROR);
        }

        return accountId;
    };

    return {
        isAdded: result.status === 'ADDED',
        isArchived: result.status === 'ARCHIVED',
        isBusy: isAdding || isArchiving,
        isUnarchiving,
        handleAdd,
        handleArchive,
        handleUnarchive,
        isCampaignPickerOpen,
        setIsCampaignPickerOpen,
        isOpeningCampaignPicker,
        resolveAccountId,
        showPendingAdd,
    };
}
